import React, {
  Fragment,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { MaskedInput } from 'rsuite';

import {
  TextField,
  Textarea,
  inputLabelClasses,
  DateOfBirthPicker,
  InputMask,
} from 'src/ui/components';

import { classNames } from 'src/utils/className';

import { ResponseType } from 'src/features/PersonRepository/types/QuestionsTabType';
import { usePersonRepoStore } from 'src/features/PersonRepository/store/personRepoStore';

export type DateOfBirthCommentsResponseModel = {
  birthdateRaw: string;
  birthdate: Date | undefined;
  age: number;
  comments: string;
};

const dobModel: DateOfBirthCommentsResponseModel = {
  birthdateRaw: '',
  birthdate: undefined,
  age: 0,
  comments: '',
};

const mask = () => [
  /0|1/,
  /\d/,
  '/',
  /[0-3]/,
  /\d/,
  '/',
  /1|2/,
  /\d/,
  /\d/,
  /\d/,
];

export function DobCommentsEdit({
  question,
}: {
  question: ResponseType;
}): ReactElement {
  const regex = useMemo(() => {
    return /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/;
  }, []);

  const qnTempId = useMemo(() => {
    return question?.TemplateId || 0;
  }, [question]);

  const formatedDobComments = usePersonRepoStore(
    React.useCallback(
      state =>
        state?.formValues?.Poc.DobComments &&
        state?.formValues?.Poc?.DobComments?.[qnTempId],
      [qnTempId],
    ),
  );

  const dobCommentsRef = useRef();

  React.useEffect(() => {
    dobCommentsRef.current = formatedDobComments;
  }, [formatedDobComments]);

  const updateDobComments = usePersonRepoStore(
    useCallback(state => state?.updateDobComments, []),
  );

  const calculateAge = (dob: string) => {
    const currentDate = new Date();
    const birthDate = new Date(dob ? dob : '');

    // Check if the input date is a valid date and not in the future
    if (isNaN(birthDate.getTime()) || birthDate > currentDate) {
      updateDobComments(
        {
          birthdateRaw: '',
          age: '',
          comments: formatedDobComments.comments || '',
        },
        qnTempId,
      );
    } else {
      if (
        dob[0] === '0' &&
        dob[1] === '2' &&
        (birthDate.getFullYear() % 4 !== 0 ||
          (birthDate.getFullYear() % 100 === 0 &&
            birthDate.getFullYear() % 400 !== 0)) &&
        dob[3] === '2' &&
        dob[4] === '9'
      )
        updateDobComments(
          {
            birthdateRaw: dob,
            age: '',
            comments: formatedDobComments.comments || '',
          },
          qnTempId,
        );
      else if (dob[0] === '0' && dob[1] === '2' && dob[3] === '3')
        updateDobComments(
          {
            birthdateRaw: dob,
            age: '',
            comments: formatedDobComments.comments || '',
          },
          qnTempId,
        );
      else if (birthDate.getFullYear() < currentDate?.getFullYear()) {
        if (currentDate?.getMonth() < birthDate.getMonth())
          updateDobComments(
            {
              birthdateRaw: dob,
              age: currentDate?.getFullYear() - birthDate.getFullYear() - 1,
              comments: formatedDobComments.comments || '',
            },
            qnTempId,
          );
        else {
          if (
            currentDate.getDate() < birthDate.getDate() &&
            currentDate?.getMonth() === birthDate.getMonth()
          )
            updateDobComments(
              {
                birthdateRaw: dob,
                age: currentDate.getFullYear() - birthDate.getFullYear() - 1,
                comments: formatedDobComments.comments || '',
              },
              qnTempId,
            );
          else
            updateDobComments(
              {
                birthdateRaw: dob,
                age: currentDate.getFullYear() - birthDate.getFullYear(),
                comments: formatedDobComments.comments || '',
              },
              qnTempId,
            );
        }
      } else {
        if (currentDate?.getMonth() > birthDate.getMonth())
          updateDobComments(
            {
              birthdateRaw: dob,
              age: currentDate?.getFullYear() - birthDate.getFullYear() + 1,
              comments: formatedDobComments.comments || '',
            },
            qnTempId,
          );
        else {
          if (
            currentDate.getDate() >= birthDate.getDate() &&
            currentDate?.getMonth() === birthDate.getMonth()
          )
            updateDobComments(
              {
                birthdateRaw: dob,
                age: currentDate.getFullYear() - birthDate.getFullYear() + 1,
                comments: formatedDobComments.comments || '',
              },
              qnTempId,
            );
          else
            updateDobComments(
              {
                birthdateRaw: dob,
                age: currentDate.getFullYear() - birthDate.getFullYear(),
                comments: formatedDobComments.comments || '',
              },
              qnTempId,
            );
        }
      }
    }
  };

  const tempMask = useMemo(() => {
    if (qnTempId) return mask();
  }, [qnTempId]);

  const handleDate = (e: any, qId: number) => {
    if (qId === qnTempId) {
      if (e[0] === '0') tempMask?.splice(1, 1, /[0-9]/);
      else tempMask?.splice(1, 1, /[0-2]/);

      if (e[3] === '0' || e[3] === '1' || e[3] === '2')
        tempMask?.splice(4, 1, /[0-9]/);
      else tempMask?.splice(4, 1, /0|1/);

      if (e[6] === '1') tempMask?.splice(7, 1, /9/);
      else tempMask?.splice(7, 1, /0/);

      if (e[9] !== '_' && regex.test(e)) calculateAge(e);
      //else setValue(`Dob.${qnTempId}.age`, "");
      else
        updateDobComments(
          {
            birthdateRaw: e,
            age: '',
            comments: formatedDobComments?.comments,
          },
          qnTempId,
        );
    }
  };

  console.log('formatedDob', formatedDobComments);

  return (
    <dd className="flex gap-2  border-b dark:border-dark-medium">
      <h4 className="w-1/3 px-3 py-2 text-sm font-medium">
        {question?.FieldLabel}
      </h4>
      <div className="grid w-4/6 grid-cols-1 gap-x-6 gap-y-3 px-3  py-2 md:grid-cols-2">
        <div className="relative [&>*]:w-full  ">
          <label
            htmlFor="date-birth"
            className={classNames(inputLabelClasses, 'mb-0.5')}
          >
            Date of birth
          </label>
          <MaskedInput
            placeholder="__/__/____"
            mask={tempMask}
            onChange={(e: any) => {
              handleDate(e, qnTempId);
            }}
            value={
              formatedDobComments && formatedDobComments['birthdateRaw']
                ? formatedDobComments['birthdateRaw']
                : ''
            }
            keepCharPositions={true}
          />
        </div>
        <TextField
          id="age"
          label="Age"
          type="number"
          value={formatedDobComments?.age}
          onChange={(e: any) => {
            updateDobComments(
              {
                ...(dobCommentsRef?.current || []),
                birthdateRaw: '',
                age: e.target.value,
              },
              qnTempId,
            );
          }}
        />
        <TextField
          id="dobComments"
          label="Comments"
          value={
            formatedDobComments && formatedDobComments['comments']
              ? formatedDobComments['comments']
              : ''
          }
          onChange={(e: any) => {
            console.log('eeeeeeeeeeeeeeeeeeee', e);
            updateDobComments(
              { ...(dobCommentsRef?.current || []), comments: e.target.value },
              qnTempId,
            );
          }}
        />
      </div>
    </dd>
  );
}
